import styled from '@emotion/styled'
import React from 'react'
import { STICKY_HEADER_SHADOW } from '../../../style/styleConstants'
import { CloseIcon } from '../../icons/Close'
import { Banner } from '../../shared/Banner'
import {
    PRIMARY_ICON_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { ApiLandingPageBannerBenefit } from '@storefronts/common-types'
import { scaledFontSize, ScrollingLayout } from '../../../style/scrollingLayout'
import { getTypographyRoleStyle } from '../../../style/typography'

type CloseSide = 'left' | 'right'

const GridHeader = styled.div({
    position: 'sticky',
    zIndex: 2,
    top: 0,
    width: '100%',
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
})

const HeaderWrapper = styled.div<{ isHidden: boolean }>(({ isHidden }) => ({
    position: 'sticky',
    top: 0,
    width: '100%',
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    zIndex: 2,
    borderBottom: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
    boxShadow: STICKY_HEADER_SHADOW,
    willChange: 'transform',
    transition: 'transform 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    transform: isHidden ? 'translateY(-100%)' : 'translateY(0)',
    // Hiding the header on scroll buys back screen on a phone; a desktop
    // window has room to keep it pinned.
    [ScrollingLayout.DESKTOP_MEDIA]: {
        transform: 'none',
        transition: 'none',
        willChange: 'auto',
    },
}))

const GridIconButton = styled.button({
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    '&:hover': {
        opacity: 0.7,
    },
})

const CenterLabel = styled.div({
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    ...getTypographyRoleStyle('navText', {
        fontWeight: 500,
    }),
    fontSize: scaledFontSize(14),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    whiteSpace: 'nowrap',
})

const GridHeaderContent = styled.div<{ desktopCloseSide: CloseSide }>(
    ({ desktopCloseSide }) => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        padding: '10px 15px 10px 20px',
        minHeight: '43px',
        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
        // Reversing the row rather than reordering the slots keeps the close
        // button first in the DOM, so it stays the first thing reached by
        // keyboard and assistive tech wherever it is drawn.
        ...(desktopCloseSide === 'right'
            ? {
                  [ScrollingLayout.DESKTOP_MEDIA]: {
                      flexDirection: 'row-reverse',
                      padding: '10px 20px 10px 15px',
                  },
              }
            : null),
    }),
)

const SlotContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    minWidth: 40,
})

interface DrawerHeaderProps {
    onClose: () => void
    centerLabel?: string
    rightSlot?: React.ReactNode
    bannerText?: string
    bannerBenefits: ApiLandingPageBannerBenefit[]
    isHeaderVisible?: boolean
    /**
     * Which end the close button sits at once there is room for the wide
     * layout. Narrow widths always keep it on the left, where a drawer's back
     * affordance belongs.
     */
    desktopCloseSide?: CloseSide
    /** Pinned with the header rather than scrolling away beneath it. */
    belowHeader?: React.ReactNode
}

export const DrawerHeader: React.FC<DrawerHeaderProps> = ({
    onClose,
    centerLabel,
    rightSlot,
    bannerText,
    bannerBenefits,
    isHeaderVisible = true,
    desktopCloseSide = 'left',
    belowHeader,
}) => {
    return (
        <HeaderWrapper isHidden={!isHeaderVisible}>
            <GridHeader>
                {bannerText && (
                    <Banner
                        bannerText={bannerText}
                        bannerBenefits={bannerBenefits}
                    />
                )}
                <GridHeaderContent desktopCloseSide={desktopCloseSide}>
                    <SlotContainer>
                        <GridIconButton onClick={onClose}>
                            <CloseIcon
                                width={28}
                                height={28}
                                fill={`var(${PRIMARY_ICON_COLOR_VAR})`}
                            />
                        </GridIconButton>
                    </SlotContainer>
                    {centerLabel && <CenterLabel>{centerLabel}</CenterLabel>}
                    <SlotContainer style={{ justifyContent: 'flex-end' }}>
                        {rightSlot}
                    </SlotContainer>
                </GridHeaderContent>
                {belowHeader}
            </GridHeader>
        </HeaderWrapper>
    )
}
